// Admin notifications config + queue view (v92).
//
// Affected developers get an automated email when a security block
// is created (or when their fetch attempt is denied). This page
// lets admins:
//   - Toggle notifications on/off
//   - Configure SMTP host/port/auth/from-address
//   - View the recent queue (subject, recipient, status)
//   - Send a test email to themselves to verify the setup

import { useEffect, useState } from 'react';
import { Mail, Send, RotateCw, CheckCircle2, XCircle, Clock, MinusCircle } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useNotificationSettings,
  useNotificationQueue,
  saveNotificationSettings,
  sendTestEmail,
  type NotificationSettings,
  type QueueStatus,
} from '../../api/notifications';

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function statusTone(s: QueueStatus): { tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string; Icon: typeof CheckCircle2 } {
  switch (s) {
    case 'sent': return { tone: 'success', label: 'sent', Icon: CheckCircle2 };
    case 'failed': return { tone: 'danger', label: 'failed', Icon: XCircle };
    case 'pending': return { tone: 'warning', label: 'pending', Icon: Clock };
    case 'skipped': return { tone: 'neutral', label: 'skipped', Icon: MinusCircle };
  }
}

function notificationStatusBadge(isLoading: boolean, enabled: boolean) {
  if (isLoading) return <Badge tone="neutral">Loading…</Badge>;
  if (enabled) {
    return (
      <Badge tone="success">
        <Mail className="h-3 w-3" />
        Enabled
      </Badge>
    );
  }
  return <Badge tone="neutral">Disabled</Badge>;
}

const DEFAULT_FORM: NotificationSettings = {
  enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_tls: 'starttls',
  from_address: '',
  from_name: 'OrbitalReg',
  notify_block_created: true,
  notify_block_hit: true,
};

// Wrapper kept for direct route compatibility — see AdminNotificationsHub
// for the consolidated Notifications IA. Routes that hit this directly
// today are redirected by App.tsx; this export remains so deep-imports
// from tests / future call sites keep working.
export function AdminNotifications() {
  return (
    <AdminShell
      title="Email notifications"
      description="When a security block is created, OrbitalReg can email developers whose project depends on the affected artifact. Configure your SMTP relay below — the queue is durable, so emails survive process restarts."
    >
      <AdminNotificationsBody />
    </AdminShell>
  );
}

// Body component for use inside the Notifications hub. Renders the
// page content sans <AdminShell>, so the hub can own the shell + tab
// strip while each tab body just contributes inner cards.
export function AdminNotificationsBody() {
  const { data, mutate, isLoading } = useNotificationSettings();
  const { data: queue, mutate: mutateQueue } = useNotificationQueue(50);
  const [form, setForm] = useState<NotificationSettings>(DEFAULT_FORM);
  const [hasPassword, setHasPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data.settings);
      setHasPassword(data.has_password);
    }
  }, [data]);

  const onSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      await saveNotificationSettings(form);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSendTest = async () => {
    setBusy(true);
    setErr(null);
    setTestStatus(null);
    try {
      const r = await sendTestEmail(testEmail || undefined);
      setTestStatus(`Test email queued for ${r.recipient}.`);
      await mutateQueue();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Notification settings"
          description="Off by default. Flip Enable on once SMTP is configured."
          actions={notificationStatusBadge(isLoading, form.enabled)}
        />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-md bg-bg-inset border border-border">
            <input
              id="notif-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="h-4 w-4"
            />
            <label htmlFor="notif-enabled" className="text-sm text-fg flex-1 cursor-pointer">
              Enable email notifications
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="notif-smtp-host" className="block text-sm font-medium text-fg mb-1.5">SMTP host</label>
              <Input
                id="notif-smtp-host"
                value={form.smtp_host}
                onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                placeholder="smtp.example.com"
              />
            </div>
            <div>
              <label htmlFor="notif-smtp-port" className="block text-sm font-medium text-fg mb-1.5">SMTP port</label>
              <Input
                id="notif-smtp-port"
                type="number"
                value={form.smtp_port}
                onChange={(e) => setForm({ ...form, smtp_port: Number.parseInt(e.target.value, 10) || 587 })}
              />
            </div>
            <div>
              <label htmlFor="notif-smtp-user" className="block text-sm font-medium text-fg mb-1.5">Username</label>
              <Input
                id="notif-smtp-user"
                value={form.smtp_user}
                onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                placeholder="apikey or noreply@example.com"
              />
            </div>
            <div>
              <label htmlFor="notif-smtp-password" className="block text-sm font-medium text-fg mb-1.5">
                Password{' '}
                {hasPassword && (
                  <span className="text-fg-subtle font-normal">(leave empty to keep current)</span>
                )}
              </label>
              <Input
                id="notif-smtp-password"
                type="password"
                value={form.smtp_password}
                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                placeholder={hasPassword ? '••••••••' : 'app password'}
              />
            </div>
            <div>
              <label htmlFor="notif-smtp-tls" className="block text-sm font-medium text-fg mb-1.5">TLS mode</label>
              <select
                id="notif-smtp-tls"
                className={selectClass + ' w-full'}
                value={form.smtp_tls}
                onChange={(e) => setForm({ ...form, smtp_tls: e.target.value as NotificationSettings['smtp_tls'] })}
              >
                <option value="starttls">STARTTLS (port 587)</option>
                <option value="tls">Implicit TLS (port 465)</option>
                <option value="none">None (plaintext, dev only)</option>
              </select>
            </div>
            <div>
              <label htmlFor="notif-from-address" className="block text-sm font-medium text-fg mb-1.5">From address</label>
              <Input
                id="notif-from-address"
                value={form.from_address}
                onChange={(e) => setForm({ ...form, from_address: e.target.value })}
                placeholder="orbitalreg@example.com"
              />
            </div>
            <div>
              <label htmlFor="notif-from-name" className="block text-sm font-medium text-fg mb-1.5">From name</label>
              <Input
                id="notif-from-name"
                value={form.from_name}
                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                placeholder="OrbitalReg"
              />
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <p className="orb-section-title text-fg-muted">When to notify</p>
            <div className="flex items-center gap-3">
              <input
                id="notif-bc"
                type="checkbox"
                checked={form.notify_block_created}
                onChange={(e) => setForm({ ...form, notify_block_created: e.target.checked })}
                className="h-4 w-4"
              />
              <label htmlFor="notif-bc" className="text-sm text-fg cursor-pointer">
                Notify project members when a new security block is created
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="notif-bh"
                type="checkbox"
                checked={form.notify_block_hit}
                onChange={(e) => setForm({ ...form, notify_block_hit: e.target.checked })}
                className="h-4 w-4"
              />
              <label htmlFor="notif-bh" className="text-sm text-fg cursor-pointer">
                Notify a developer when their fetch attempt is denied by a block
              </label>
            </div>
          </div>
        </CardBody>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="primary" disabled={busy} onClick={onSave}>
            <Mail className="h-4 w-4" />
            Save settings
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title="Send a test email"
          description="Queues an immediate test email to verify your SMTP setup. Defaults to your own admin address if left empty."
        />
        <CardBody>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label htmlFor="notif-test-recipient" className="block text-sm font-medium text-fg mb-1.5">Recipient (optional)</label>
              <Input
                id="notif-test-recipient"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="leave empty to send to yourself"
              />
            </div>
            <Button variant="secondary" disabled={busy} onClick={onSendTest}>
              <Send className="h-4 w-4" />
              Send test
            </Button>
          </div>
          {testStatus && (
            <p className="mt-3 text-sm text-success">{testStatus}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Recent queue (${queue?.length ?? 0})`}
          description="Auto-refreshed every 15 seconds. Skipped rows ran while SMTP was disabled — they are never retried automatically."
          actions={
            <button
              type="button"
              onClick={() => mutateQueue()}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Status</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Kind</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Recipient</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Subject</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Created</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Attempts</th>
              </tr>
            </thead>
            <tbody>
              {queue?.length ? (
                queue.map((row) => {
                  const s = statusTone(row.Status);
                  const Icon = s.Icon;
                  return (
                    <tr key={row.ID} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                      <td className="px-5 py-3">
                        <Badge tone={s.tone}>
                          <Icon className="h-3 w-3" />
                          {s.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <span className="orb-code text-xs">{row.Kind}</span>
                      </td>
                      <td className="px-5 py-3 text-fg text-xs">{row.RecipientEmail}</td>
                      <td className="px-5 py-3 text-fg-muted text-xs max-w-md truncate">{row.Subject}</td>
                      <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                        {new Date(row.CreatedAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-fg-muted text-xs">
                        {row.Attempts}
                        {row.LastError && (
                          <span className="block text-danger text-[10px] mt-0.5 truncate max-w-xs" title={row.LastError}>
                            {row.LastError}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No notifications queued yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {err && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{err}</p>
          </CardBody>
        </Card>
      )}
    </>
  );
}
